import { useMemo } from 'react';
import { useNetworkAccountsListQuery } from 'store/redux/apis/network-accounts/network-accounts.api';

/**
 * Returns all network accounts data
 * including default account data
 * @typedef NetworkAccountHookData
 * @property {Array<NetworkAccount>} accounts
 * @property {Boolean} accounts.isLoading
 *
 * @returns {NetworkAccountHookData & RTKQueryHook}
 */
export default function useNetworkAccounts(query = { active: true }) {
  const {
    data: allAccountsResponse = [],
    isFetching,
    isError,
    refetch,
  } = useNetworkAccountsListQuery(query);

  const allAccounts = Array.isArray(allAccountsResponse)
    ? allAccountsResponse
    : allAccountsResponse?.data || [];

  const bingAccounts = useMemo(() => {
    return allAccounts.filter((acc) => acc.network === 'bing');
  }, [allAccounts]);

  const tiktokAccounts = useMemo(() => {
    return allAccounts.filter((acc) => acc.network === 'tiktok');
  }, [allAccounts]);

  const googleAccounts = useMemo(() => {
    return allAccounts.filter((acc) => acc.network === 'google');
  }, [allAccounts]);

  const facebookAccounts = useMemo(() => {
    return allAccounts.filter((acc) => acc.network === 'facebook');
  }, [allAccounts]);

  return {
    accounts: allAccounts,
    google: googleAccounts,
    facebook: facebookAccounts,
    bing: bingAccounts,
    tiktok: tiktokAccounts,
    isLoading: isFetching,
    isError,
    refetch,
  };
}

export const useAllNetworkAccounts = (forselect = false) => {
  const { google, facebook, isLoading } = useNetworkAccounts();

  if (isLoading) {
    return [];
  }

  if (forselect) {
    return [...google, ...facebook].reduce((prev, acc) => {
      prev[acc.id.toString()] = { name: acc.name, value: acc.id.toString() };
      return prev;
    }, {});
  }

  return [...google, ...facebook];
};
